import PropTypes from 'prop-types';
import React, { useState } from 'react';
import ReactSelect from 'react-select';
import { useIntl, defineMessages } from 'react-intl';

import { tokens } from '@companycam/slab';
import { Button, Modal, Notice, Text } from '@companycam/slab-web';

import { REACT_SELECT_STYLES } from 'helpers/styles';
import Common from 'helpers/intl-common';
import Steven, { ELEMENT_TYPES } from 'helpers/steven';

import useCreateIntegrationActionRequest from './useCreateIntegrationActionRequest';
import { useGetQuickbooksOnlineResources } from './useGetQuickbooksOnlineResources';

const messages = defineMessages({
  send: {
    id: 'QBO.modal.send',
    defaultMessage: 'Send',
    description: 'Send',
  },
  selectRecord: {
    id: 'QBO.modal.selectRecord',
    defaultMessage:
      'Select the invoice, estimate, or customer record you want to send these photos to:',
    description: 'Select record',
  },
  selectPlaceholder: {
    id: 'QBO.modal.selectPlaceholder',
    defaultMessage: 'Select an item',
    description: 'Select dropdown placeholder',
  },
  noCustomerRecordFound: {
    id: 'QBO.modal.noCustomerRecord',
    defaultMessage:
      "Looks like you don't have a corresponding Quickbooks customer record for this project, so a new record will be created for you.",
    description: 'No customer record',
  },
  noInvoicesOrEstimates: {
    id: 'QBO.modal.noInvoicesOrEstimates',
    defaultMessage:
      "Looks like you don't have any invoices or estimates for the corresponding Quickbooks customer. You can still send photos to the customer record though.",
    description: 'No invoices or estimates',
  },
  customerRecord: {
    id: 'QBO.modal.customerRecord',
    defaultMessage: 'Customer Record',
    description: 'Customer Record',
  },
  invoice: {
    id: 'QBO.modal.invoice',
    defaultMessage: 'Invoice',
    description: 'Invoice',
  },
  estimate: {
    id: 'QBO.modal.estimate',
    defaultMessage: 'Estimate',
    description: 'Estimate',
  },
});

const QuickbooksOnlineModal = ({
  title,
  onRequestClose,
  projectDatabaseId,
  projectNodeId,
  integrationActionId,
  requestArgs,
}) => {
  const intl = useIntl();

  const { createIntegrationActionRequest } = useCreateIntegrationActionRequest({
    integrationActionId,
    projectId: projectNodeId,
    requestArgs: JSON.stringify(requestArgs || {}),
  });

  const {
    data: qboData,
    isLoading,
    isError,
  } = useGetQuickbooksOnlineResources(projectDatabaseId);

  const [selectedItem, setSelectedItem] = useState(null);
  const selectionOptions = [];
  let noticeMessage = null;

  // If we don't got a certain type of QBO record, we need to tell the user what we don't got
  if (!qboData?.customer) {
    noticeMessage = intl.formatMessage(messages.noCustomerRecordFound);
  } else if (
    qboData?.invoices.length === 0 &&
    qboData?.estimates.length === 0
  ) {
    noticeMessage = intl.formatMessage(messages.noInvoicesOrEstimates);
  }

  // if a customer record exists, make that a selectable choice
  if (qboData?.customer) {
    selectionOptions.push({
      label: `${qboData.customer.name} ${intl.formatMessage(
        messages.customerRecord,
      )}`,
      value: {
        target_name: qboData.customer.name,
        target_source_id: qboData.customer.externalIdentifier,
        target_type: 'customer',
      },
    });
  }

  // and if any estimates exist, make them selectable choices
  if (qboData?.estimates?.length) {
    qboData.estimates.forEach((estimate) => {
      selectionOptions.push({
        label: `${intl.formatMessage(messages.estimate)} #${estimate.number}`,
        value: {
          target_name: `Estimate #${estimate.number}`,
          target_source_id: estimate.externalIdentifier,
          target_type: 'estimate',
        },
      });
    });
  }

  // and if any invoices exist, make them selectable choices too
  if (qboData?.invoices?.length) {
    qboData.invoices.forEach((invoice) => {
      selectionOptions.push({
        label: `${intl.formatMessage(messages.invoice)} #${invoice.number}`,
        value: {
          target_name: `Invoice #${invoice.number}`,
          target_source_id: invoice.externalIdentifier,
          target_type: 'invoice',
        },
      });
    });
  }

  const onSelectionChange = (newValue) => {
    setSelectedItem(newValue);
  };

  const sendPhotosToQBORecord = () => {
    Steven.elementClicked({
      id: 'quickbooks_online_modal_send_button',
      content: intl.formatMessage(messages.send),
      type: ELEMENT_TYPES.BUTTON,
      action: 'photos_sent_to_quickbooks_online',
    });

    const rawRequestArgs = {
      ...requestArgs,
      ...(selectedItem?.value || {}), // there won't be a selectedItem if there's no customer record
    };
    const stringifiedRequestArgs = JSON.stringify(rawRequestArgs);

    createIntegrationActionRequest({
      variables: {
        input: {
          integrationActionId,
          projectId: projectNodeId,
          requestArgs: stringifiedRequestArgs,
        },
      },
    });
    onRequestClose();
  };

  /*
    Only disable the Send button if the GET request hasn't resolved,
    the user hasn't made a selection,
    or there isn't any data to select in the first place
  */
  const sendButtonDisabled = isLoading || (qboData && !selectedItem);

  // only show the selection dropdown if there are QBO records from which to select
  const showDropdown = isLoading || !isError;

  return (
    <Modal
      isOpen
      title={title}
      showCloseButton
      shouldCloseOnOverlayClick
      size="small"
      bodyStyle={{
        display: 'flex',
        flexGrow: 1,
        flexDirection: 'column',
        gap: tokens.size_spacing_m,
        overflowY: 'initial',
      }}
      onClose={onRequestClose}
      footerActions={
        <>
          <Button color="subtle" onClick={onRequestClose}>
            {intl.formatMessage(Common.cancel)}
          </Button>
          <Button
            color="primary"
            disabled={sendButtonDisabled}
            onClick={sendPhotosToQBORecord}
          >
            {intl.formatMessage(messages.send)}
          </Button>
        </>
      }
    >
      <Text size="s">{intl.formatMessage(messages.selectRecord)}</Text>

      {/* Only display the notice if there's a notice to display after the GET request has finished */}
      {!isLoading && noticeMessage !== null && (
        <Notice color="caution">{noticeMessage}</Notice>
      )}

      {showDropdown ? (
        <ReactSelect
          isLoading={isLoading}
          isDisabled={isLoading}
          isMulti={false}
          styles={REACT_SELECT_STYLES}
          placeholder={intl.formatMessage(messages.selectPlaceholder)}
          options={selectionOptions}
          onChange={onSelectionChange}
        />
      ) : null}
    </Modal>
  );
};

QuickbooksOnlineModal.propTypes = {
  onRequestClose: PropTypes.func,
  title: PropTypes.string,
  projectDatabaseId: PropTypes.number,
  projectNodeId: PropTypes.string,
  integrationActionId: PropTypes.string,
  requestArgs: PropTypes.object,
};

export default QuickbooksOnlineModal;
